import { useEffect, useRef } from 'react';

import { useActiveElement } from '../../hooks/useActiveElement.js';
import { useFocusContext } from '../../hooks/useFocusContext.js';
import { useModality } from '../../hooks/useModality.js';
import { type DataTestId } from '../../types/data-props.js';
import type { WithChildren } from '../../types/with-children.js';
import { focusFirstDescendant } from '../../utils/focus-management/focus-first-descendant.js';
import { getFirstFocusableChild } from '../../utils/focus-management/get-first-focusable-child.js';
import { getLastFocusableChild } from '../../utils/focus-management/get-last-focusable-child.js';

// #region Types

/**
 * Prop definition of the `FocusScope` component.
 * @public
 */
export interface FocusScopeProps extends WithChildren, DataTestId {
  /**
   * If set to true, the focus scope automatically focuses the first focusable element inside.
   * @defaultValue false
   */
  autoFocus?: boolean;
  /**
   * If set to true, the focus scope traps the focus inside.
   * @defaultValue false
   */
  contain?: boolean;
}
// #endregion Types

function isInInertSubtree(element: Element | null): boolean {
  let node: Element | null = element;
  while (node) {
    if (node.hasAttribute('inert')) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/**
 * The `FocusScope` helps you manage the focus when opening any overlay. If you
 * want to create a custom overlay, you can wrap the content of your overlay in the
 * `FocusScope` to help you make the overlay accessible and handle focus
 * appropriately.
 * @public
 */
export const FocusScope = (props: FocusScopeProps) => {
  const { autoFocus, contain, children, 'data-testid': dataTestId } = props;
  const focusContext = useFocusContext();
  const modality = useModality();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusedElement = useActiveElement();
  const triggerElement = useRef(focusedElement);

  let focusOptions: { focusVisible: boolean } | undefined;

  // If the modality is keyboard, we want to focus the first focusable element and show the focus ring.
  if (modality === 'keyboard') {
    focusOptions = { focusVisible: true };
  } else if (
    // If the modality is pointer or touch, we want to focus the first focusable element but not show the focus ring.
    modality === 'pointer' ||
    modality === 'touch'
  ) {
    focusOptions = { focusVisible: false };
  }

  // Keep a ref so event handlers always read the current modality-derived value,
  // even when modality changes while the scope is open (effect deps don't include modality).
  const focusOptionsRef = useRef(focusOptions);
  focusOptionsRef.current = focusOptions;

  useEffect(() => {
    if (contain) {
      const triggerElementCopy = triggerElement.current;

      // Register on the active-scope stack BEFORE moving focus, so the
      // previous top scope's focusin listener sees this scope as the new top
      // and yields rather than stealing focus back.
      const scopeRegistration = {
        wrapperRef,
        triggerElement: triggerElementCopy,
      };
      const unregister = focusContext.registerScope(scopeRegistration);
      focusContext.setBackgroundInertness(true);

      // Containment listener: only the top scope acts. When focus escapes
      // into another registered scope (e.g. a closing menu restoring focus
      // to a trigger that lives in an outer Sheet), pull it back. Focus
      // moving into a sibling portal that isn't a FocusScope (e.g. a Radix
      // Menu's portaled content) is a legitimate nested overlay — leave it
      // alone, otherwise the menu's onSelect loses focus and never fires.
      const handleFocusIn = (event: FocusEvent) => {
        if (focusContext.getTopScope() !== scopeRegistration) {
          return;
        }
        const target = event.target as Element | null;
        if (target && wrapperRef.current?.contains(target)) {
          return;
        }
        // If focus landed on an element inside an inert subtree, an async
        // operation (e.g. Radix restoring focus to a menu trigger after
        // onSelect fires) placed focus in the now-inert background.
        // Pull it back into this scope.
        if (isInInertSubtree(target)) {
          focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
          return;
        }
        if (!focusContext.isInsideAnyScope(target)) {
          return;
        }
        focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
      };
      document.addEventListener('focusin', handleFocusIn, true);

      // Tab cycling: prevent focus from escaping the scope (e.g. to parent
      // frames in an iframe-hosted app) by wrapping Tab/Shift+Tab at the
      // boundaries of the scope.
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') {
          return;
        }
        if (focusContext.getTopScope() !== scopeRegistration) {
          return;
        }
        const wrapper = wrapperRef.current;
        if (!wrapper) {
          return;
        }
        const first = getFirstFocusableChild(wrapper) as HTMLElement | null;
        const last = getLastFocusableChild(wrapper) as HTMLElement | null;
        if (!first || !last) {
          return;
        }
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus({ preventScroll: true, ...focusOptionsRef.current });
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus({ preventScroll: true, ...focusOptionsRef.current });
          }
        }
      };
      document.addEventListener('keydown', handleKeyDown, true);

      focusFirstDescendant(wrapperRef.current, focusOptions);

      return () => {
        document.removeEventListener('focusin', handleFocusIn, true);
        document.removeEventListener('keydown', handleKeyDown, true);
        unregister();
        focusContext.setBackgroundInertness(false);

        // Only restore focus to this scope's trigger when it would not steal
        // focus from another still-active scope: if a remaining scope already
        // owns focus, leave it alone.
        const stillActive = focusContext.getTopScope();
        if (
          !stillActive?.wrapperRef.current?.contains(document.activeElement)
        ) {
          focusContext.focusTriggerElement(triggerElementCopy);
        }
      };
    }

    if (autoFocus) {
      focusFirstDescendant(wrapperRef.current, focusOptions);
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      if (e.target === triggerElement.current) {
        e.preventDefault();
        focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
      }
    };

    if (!autoFocus && !contain) {
      document.addEventListener('keydown', handleTabKey);
    }

    const triggerElementCopy = triggerElement.current;

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      // restore focus on trigger when unmounting the focusscope
      triggerElementCopy.focus();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contain, autoFocus]);

  return (
    <div
      style={{ display: 'contents' }}
      ref={wrapperRef}
      aria-labelledby={triggerElement.current.id}
      data-testid={dataTestId}
      // Prevents that focus is lost when clicking on non-focusable items inside (atm used in the internal Drawer).
      // Probably worth considering moving that to the focusScope when redoing the overlays.
      // tabIndex={-1}
    >
      {children}
    </div>
  );
};

FocusScope.displayName = 'FocusScope';
